// =============================================================================
// Catalog Index for Smart Parsing (Q-304)
// Hydrates client/contact data into Fuse.js indexes once per module load.
// =============================================================================

import { useMemo } from 'react';
import Fuse from 'fuse.js';
import { useQuery } from '@tanstack/react-query';
import { clientesService } from '@/services/clientes';
import { actividadesService } from '@/services/actividades';
import { FUSE_CONFIG } from '@/lib/fuseConfig';
import type { Cliente, Contacto, TipoActividadRef } from '@/types';

export interface CatalogIndex {
  clientes: Fuse<Cliente>;
  contactos: Fuse<Contacto>;
  tiposActividad: Fuse<TipoActividadRef>;
  isLoading: boolean;
  isError: boolean;
  clientesList: readonly Cliente[];
  contactosList: readonly Contacto[];
  tiposActividadList: readonly TipoActividadRef[];
}

const EMPTY_CLIENTS: Cliente[] = [];
const EMPTY_CONTACTS: Contacto[] = [];
const EMPTY_TIPOS: TipoActividadRef[] = [];

export function useCatalogIndex(): CatalogIndex {
  const { data: clientesData, isLoading: loadingClientes, isError: errorClientes } = useQuery({
    queryKey: ['catalogIndex', 'clientes'],
    queryFn: () => clientesService.list({ page_size: 250, activo: true }),
    staleTime: 10 * 60 * 1000, // 10 min cache
  });

  const { data: contactosData, isLoading: loadingContactos, isError: errorContactos } = useQuery({
    queryKey: ['catalogIndex', 'contactos'],
    queryFn: () => clientesService.getAllContactos(250),
    staleTime: 10 * 60 * 1000,
  });

  const { data: tiposData, isLoading: loadingTipos, isError: errorTipos } = useQuery({
    queryKey: ['catalogIndex', 'tiposActividad'],
    queryFn: () => actividadesService.getTipos(),
    staleTime: 10 * 60 * 1000,
  });

  const clientesList = clientesData?.items ?? EMPTY_CLIENTS;
  const contactosList = contactosData ?? EMPTY_CONTACTS;
  const tiposActividadList = tiposData ?? EMPTY_TIPOS;
  const isLoading = loadingClientes || loadingContactos || loadingTipos;
  const isError = errorClientes || errorContactos || errorTipos;

  const clientes = useMemo(
    () => new Fuse(clientesList, FUSE_CONFIG.cliente),
    [clientesList],
  );

  const contactos = useMemo(
    () => new Fuse(contactosList, FUSE_CONFIG.contacto),
    [contactosList],
  );

  const tiposActividad = useMemo(
    () => new Fuse(tiposActividadList, FUSE_CONFIG.tipoActividad),
    [tiposActividadList],
  );

  return { clientes, contactos, tiposActividad, isLoading, isError, clientesList, contactosList, tiposActividadList };
}
